import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { SovColumnMappingSuggestion } from "../../model/maia/sov-column-mapping";
import { FieldMappingDecision, FieldMatchStatus, SovFieldMapping } from "../../model/maia/sov-field-mapping";

/** One entry of the `POST /sov/{jobId}/fields` payload. */
interface SovFieldSubmission {
    originalName: string;
    newName: string | null;
    type: string | null;
}

/**
 * MAIA SOV column mapping — Step 4 ("Prepare your SOV").
 *
 * `getFieldMappings` calls `POST /sov/{jobId}/column-mapping` and shapes the
 * raw suggestions into `SovFieldMapping[]`, the row model
 * MaiaPrepareStepComponent already takes as an `@Input`.
 *
 * Every row is passed through, including `rootField`/`coordinateField`/`idField` ones —
 * those are hidden from the table by MaiaPrepareStepComponent, not filtered
 * out here, since the data is expected to be needed again later.
 *
 * `submitFields` is "Accept and Continue" — `POST /sov/{jobId}/fields`,
 * carrying every row (visible and hidden alike), each with whatever name
 * the user ended up with (edited or not).
 */
@Injectable({
    providedIn: "root",
})
export class MaiaColumnMappingService {
    constructor(private readonly http: HttpClient) {}

    getFieldMappings(jobId: number): Observable<SovFieldMapping[]> {
        return this.http
            .post<SovColumnMappingSuggestion[]>(`/sov/${jobId}/column-mapping`, {})
            .pipe(map((suggestions) => suggestions.map((suggestion) => this.toFieldMapping(suggestion))));
    }

    /**
     * JIRA-SOV-015: the job's mapping as currently stored (`GET /sov/{jobId}/column-mapping`) - used to
     * reload after an edit, since saving one row can rename another (its name was taken).
     */
    reloadFieldMappings(jobId: number): Observable<SovFieldMapping[]> {
        return this.http
            .get<SovColumnMappingSuggestion[]>(`/sov/${jobId}/column-mapping`)
            .pipe(map((suggestions) => suggestions.map((suggestion) => this.toFieldMapping(suggestion))));
    }

    /**
     * JIRA-SOV-015: saves one row's MAPTYCS field (`PATCH /sov/{jobId}/column-mapping`) from the label
     * the user typed - the backend derives the field id from it (existing dataset field, else taxonomy
     * field, else snake_case of the text), keeps names unique (another column already using it gets a
     * new AI name) and answers 400 when the row is locked or the name belongs to a locked column.
     * The request field is still called `suggestedName`; it carries the typed label.
     */
    updateFieldMapping(jobId: number, rawColumnName: string, label: string): Observable<SovColumnMappingSuggestion> {
        return this.http.patch<SovColumnMappingSuggestion>(`/sov/${jobId}/column-mapping`, { rawColumnName, suggestedName: label });
    }

    submitFields(jobId: number, datasetId: string, mappings: SovFieldMapping[]): Observable<void> {
        const payload: SovFieldSubmission[] = mappings.map((mapping) => ({
            originalName: mapping.sourceField,
            newName: mapping.maiaFieldId,
            type: mapping.type,
        }));

        return this.http.post<void>(`/sov/${jobId}/fields?datasetId=${datasetId}`, payload);
    }

    private toFieldMapping(suggestion: SovColumnMappingSuggestion): SovFieldMapping {
        // Nothing matched — fall back to the raw column name so the (required)
        // MAIA Field text box never starts out blank.
        const maiaFieldId = suggestion.suggestedName === "none" && !suggestion.matched
            ? suggestion.rawColumnName
            : suggestion.suggestedName;

        return {
            sourceField: suggestion.rawColumnName,
            maiaFieldId,
            status: suggestion.matched ? FieldMatchStatus.CONFIRMED : FieldMatchStatus.UNMATCHED,
            note: suggestion.explanation,
            decision: FieldMappingDecision.ACCEPTED,
            rootField: suggestion.rootField,
            coordinateField: suggestion.coordinateField,
            idField: suggestion.idField,
            existingField: !!suggestion.existingField,
            maiaFieldLabel: suggestion.suggestedLabel || maiaFieldId,
            locked: !!suggestion.locked || !!suggestion.rootField || !!suggestion.coordinateField || !!suggestion.idField,
            type: suggestion.type,
        };
    }
}
